import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList } from "@/mocks/anime";
import { getHotAnime, searchAnime } from "@/utils/anime";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const sortTabs = [
  { value: "relevance", label: "相关度" },
  { value: "update", label: "更新时间" },
  { value: "rating", label: "评分" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [sort, setSort] = useState("relevance");

  useDocumentMeta(
    keyword ? `搜索“${keyword}” - 动漫免费在观看` : "搜索动漫 - 动漫免费在观看",
    keyword
      ? `在动漫免费在观看搜索“${keyword}”相关的动漫作品，查看搜索结果并在线观看高清动漫。`
      : "在动漫免费在观看搜索你想看的动漫，快速找到高清动漫资源。",
    "搜索动漫,动漫搜索,免费动漫,动漫免费在观看",
  );

  const results = useMemo(() => searchAnime(animeList, keyword, sort), [keyword, sort]);
  const hotList = useMemo(() => getHotAnime(4), []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors flex items-center gap-1">
          <i className="ri-home-4-line"></i>
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900 font-medium">搜索</span>
      </nav>

      <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          搜索动漫
        </h1>
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-4 py-2.5 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入动漫名称、类型或关键词"
              className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            {input && (
              <button
                type="button"
                onClick={() => setInput("")}
                className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
                aria-label="清空"
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      {keyword ? (
        <div className="mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              关键词 “<span className="text-primary-600 font-medium">{keyword}</span>” 共找到{" "}
              <span className="font-semibold text-foreground-950">{results.length}</span> 个结果
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 self-start">
              {sortTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setSort(tab.value)}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    sort === tab.value ? "bg-background-50 text-primary-600 font-medium" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {results.length === 0 ? (
            <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </div>
              <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">没有找到相关动漫</h2>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或者看看这些热门推荐。</p>
              <div className="mt-6 px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-3xl mx-auto text-left">
                {hotList.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} showSummary={false} />
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {results.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8">
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-fire-fill text-primary-500"></i>
            大家都在看
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {hotList.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}